import { useEffect, useState } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { getLegalDoc, LEGAL_LINKS, LEGACY_REDIRECTS } from "@/lib/legal";
import { DocBlocks } from "@/components/legal/DocBlocks";

const LOGO =
  "https://customer-assets.emergentagent.com/job_data-driven-baseball/artifacts/94x88gnl_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2003_59_37%20PM.png";
const COMPANY = "Rep Lab LLC";
const CONTACT = "info@replabbaseball.com";

function StatusBanner({ doc }) {
  if (doc.status === "archived" || doc.status === "superseded")
    return <p data-testid="legal-archived-banner" className="mt-6 rounded-2xl bg-white/5 border border-white/15 px-4 py-3 text-sm font-semibold text-white/70">{doc.status === "superseded" ? "Previous" : "Archived"} version {doc.version}. This version has been superseded and is kept for historical registrations.{doc.placeholder ? " Final approved copy was never published for this version." : ""}</p>;
  return null;
}

function VersionList({ doc, slug }) {
  const others = (doc.versions || []).filter((v) => v.version !== doc.version);
  if (!others.length) return null;
  return (
    <div className="mt-10">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Other Versions</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {others.map((v) => (
          <Link key={v.version} to={v.status === "archived" || v.status === "superseded" ? `/legal/${slug}?version=${v.version}` : `/legal/${slug}`} data-testid={`legal-version-${v.version}`}
            className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/60 hover:text-white transition-colors">
            v{v.version}{v.status === "archived" || v.status === "superseded" ? " (previous)" : " (current)"}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function LegalPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const version = params.get("version");
  const [doc, setDoc] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (LEGACY_REDIRECTS[slug] && !version) return;
    setDoc(null);
    setMissing(false);
    getLegalDoc(slug, version).then((d) => {
      setDoc(d);
      if (window.location.hash) setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView(), 100);
    }).catch(() => setMissing(true));
  }, [slug, version]);

  if (LEGACY_REDIRECTS[slug] && !version) return <Navigate to={`/legal/${LEGACY_REDIRECTS[slug]}`} replace />;
  if (missing) return <Navigate to="/legal/terms" replace />;

  return (
    <div className="min-h-[100svh] bg-[#0B0F19]">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center">
            <img src={LOGO} alt="Rep Lab" className="h-14 w-auto object-contain mix-blend-screen" />
          </Link>
          <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {!doc ? (
          <div className="py-24 flex justify-center"><Loader2 size={28} className="animate-spin text-white/40" /></div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2563EB]">{doc.header?.[0] || COMPANY}</p>
            <h1 data-testid="legal-title" className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tighter text-white leading-[1.02]">{doc.title}</h1>
            {(doc.header || []).slice(2).map((h, i) => (
              <p key={i} className="mt-3 text-sm font-semibold text-white/50 break-words">{h}</p>
            ))}
            <p data-testid="legal-version" className="mt-3 text-xs font-bold uppercase tracking-wider text-white/35">Version {doc.version}{doc.effective_date && !(doc.header || []).some((h) => h.includes("Effective")) ? ` · Effective ${doc.effective_date}` : ""}</p>
            <StatusBanner doc={doc} />
            {doc.is_template && (
              <p data-testid="legal-template-note" className="mt-6 rounded-2xl bg-[#2563EB]/10 border border-[#2563EB]/30 px-4 py-3 text-sm font-semibold text-white/75">This is the standard template. During registration it is automatically completed with the selected program's details, and you accept it separately for each purchase.</p>
            )}
            <div data-testid="legal-body" className="mt-6">
              <DocBlocks blocks={doc.blocks} placeholderText={doc.is_template ? "Completed automatically at registration" : undefined} />
            </div>
            <VersionList doc={doc} slug={slug} />
          </>
        )}

        <div className="mt-16 border-t border-white/10 pt-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">More Legal</p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {LEGAL_LINKS.filter((l) => l.slug !== slug || version).map((l) => (
              <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={`legal-crosslink-${l.slug}`}
                className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-white/70 hover:text-white hover:border-white/30 transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm font-medium text-white/40">© {new Date().getFullYear()} {COMPANY}. Don't just chase the results.</p>
          <a href={`mailto:${CONTACT}`} className="text-sm font-semibold text-white/60 hover:text-white transition-colors">{CONTACT}</a>
        </div>
      </footer>
    </div>
  );
}
